// HeroSection.jsx
import React from "react";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { useAuth } from "../../hooks/useAuth";
import { SITE_NAME } from "./landingConstants";
import { HeroSectionOuter, SectionInner, CenteredContent, landingSpacing } from "./LandingLayout";

/* =========================
   Styled components
   ========================= */

const Section = styled(HeroSectionOuter)`
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
  border-bottom: 1px solid #e1e8ed;

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
      transition: none !important;
    }
  }
`;

const heroEnter = keyframes`
  from { opacity: 0; transform: translateY(28px); filter: blur(4px); }
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

const HeroContent = styled(CenteredContent)``;

const HeroKicker = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid #b9d8c0;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.32rem 0.72rem;
  animation: ${heroEnter} 680ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const Title = styled.h1`
  margin: ${landingSpacing.gap.md} 0 0;
  font-family: 'Poppins', 'Roboto', sans-serif;
  font-size: clamp(2.2rem, 5vw, 4rem);
  line-height: 1.1;
  font-weight: 800;
  color: #1a2e40;
  letter-spacing: -0.025em;
  animation: ${heroEnter} 760ms 120ms cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 7vw, 2.8rem);
  }
`;

const Subtitle = styled.p`
  margin: ${landingSpacing.gap.md} auto 0;
  max-width: 680px;
  color: #4a5568;
  font-size: clamp(1.1rem, 1.8vw, 1.4rem);
  line-height: 1.6;
  font-weight: 400;
  letter-spacing: 0;
  animation: ${heroEnter} 760ms 240ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const Highlights = styled.ul`
  margin: ${landingSpacing.gap.lg} auto 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: ${landingSpacing.gap.xs};
  width: fit-content;
  text-align: left;
  animation: ${heroEnter} 760ms 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const HighlightItem = styled.li`
  position: relative;
  padding-left: 1.25rem;
  color: #1a2e40;
  font-weight: 600;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.48rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #4caf50;
    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.18);
  }
`;

const Actions = styled.div`
  margin-top: ${landingSpacing.gap.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${landingSpacing.gap.sm};
  animation: ${heroEnter} 760ms 480ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

/* Button base used for Link / anchor / button */
const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 0.62rem 1.1rem;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
`;

/* Primary (accent) */
const PrimaryLink = styled(Link)`
  ${buttonBase}
  background: #4caf50;
  color: #ffffff;
  border-radius: 8px;
  box-shadow: var(--shadow-sm, 0 2px 6px rgba(26, 46, 64, 0.12));
  opacity: 1;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

/* Secondary (outline) - anchor fallback for in-page link */
const SecondaryAnchor = styled.a`
  ${buttonBase}
  background: #ffffff;
  color: #2a4c6d;
  border-color: #d6dee6;
  border-radius: 8px;
  opacity: 1;
`;

/* =========================
   Static content to match CSS/file text
   ========================= */

const HERO_HIGHLIGHTS = [
  "Journal alimentaire",
  "Suivi du sommeil et du poids",
  "Partage avec vos amis"
];

/* =========================
   Component
   ========================= */

const HeroSection = () => {
  const { user } = useAuth();

  return (
    <Section>
      <SectionInner>
        <HeroContent>
          <HeroKicker>{SITE_NAME} · Nutrition et bien-être</HeroKicker>

          <Title>Reprenez le contrôle de votre nutrition.</Title>

          <Subtitle>
            {SITE_NAME} transforme vos habitudes en résultats concrets : mieux manger, mieux comprendre, mieux progresser.
          </Subtitle>

          <Highlights aria-hidden={HERO_HIGHLIGHTS.length === 0}>
            {HERO_HIGHLIGHTS.map((highlight) => (
              <HighlightItem key={highlight}>{highlight}</HighlightItem>
            ))}
          </Highlights>

          <Actions role="group" aria-label="Actions principales">
            {user ? (
              <PrimaryLink to="/nutrition" aria-label={`Accéder à ${SITE_NAME}`}>
                Accéder à mon espace
              </PrimaryLink>
            ) : (
              <>
                <PrimaryLink to="/register" aria-label={`Essayer ${SITE_NAME}`}>
                  Essayer {SITE_NAME}
                </PrimaryLink>

                <SecondaryAnchor href="#interface" aria-label="Découvrir l’interface">
                  Découvrir l’interface
                </SecondaryAnchor>
              </>
            )}
          </Actions>

        </HeroContent>
      </SectionInner>
    </Section>
  );
};

export default HeroSection;
